import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { HiSun, HiMoon, HiMenu, HiX } from 'react-icons/hi'

const Header = ({ 
  showSidebarToggle = false, 
  onSidebarToggle = null,
  sidebarOpen = false 
}) => {
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const appName = process.env.REACT_APP_APP_NAME || 'CitySeeCityFix'

  // Determine if we're on a public page or authenticated page
  const isPublicPage = !location.pathname.startsWith('/dashboard') && 
                      !location.pathname.startsWith('/user-admin') &&
                      !location.pathname.startsWith('/issue-admin') &&
                      !location.pathname.startsWith('/city-admin') &&
                      !location.pathname.startsWith('/app-admin') &&
                      !location.pathname.startsWith('/user-profile')

  // Public page navigation items
  const publicNavItems = [
    { name: 'Home', href: '/', show: location.pathname !== '/' },
    { name: 'For Cities', href: '/cities', show: location.pathname !== '/cities' },
    { name: 'FAQ', href: '/faq', show: location.pathname !== '/faq' },
    { name: 'Meet Maggie', href: '/maggie', show: location.pathname !== '/maggie' },
    { name: 'Login', href: '/login', show: location.pathname !== '/login' }
  ]

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            {/* Sidebar toggle for authenticated pages */}
            {showSidebarToggle && (
              <button
                onClick={onSidebarToggle}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {sidebarOpen ? <HiX className="h-6 w-6" /> : <HiMenu className="h-6 w-6" />}
              </button>
            )}
            
            {/* Logo */}
            <div className="flex items-center ml-4 lg:ml-0">
              <img
                className="h-8 w-8 rounded-full"
                src="/maggie_round.png"
                alt="CitySeeCityFix"
              />
              <span className="ml-3 text-xl font-bold text-gray-900 dark:text-white">
                {appName}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {isDark ? <HiSun className="h-6 w-6" /> : <HiMoon className="h-6 w-6" />}
            </button>
            
            {/* Navigation */}
            {isPublicPage ? (
              <>
                {/* Public page navigation */}
                <nav className="hidden md:flex space-x-8">
                  {publicNavItems.map((item) => (
                    item.show && (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </nav>
                
                {/* Get Started button for public pages */}
                <Link
                  to="/login"
                  className="btn-primary hidden md:inline-block"
                >
                  Get Started
                </Link>
                
                {/* Mobile menu button for public pages */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {mobileMenuOpen ? <HiX className="h-6 w-6" /> : <HiMenu className="h-6 w-6" />}
                </button>
              </>
            ) : (
              <>
                {/* Authenticated page user info */}
                <div className="flex items-center space-x-3">
                  <Link
                    to="/user-profile"
                    className="text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer"
                  >
                    {user?.full_name || user?.email || 'User'}
                  </Link>
                  <button
                    onClick={logout}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile menu for public pages */}
      {isPublicPage && mobileMenuOpen && (
        <>
          {/* Mobile menu overlay */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-gray-600 bg-opacity-75"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Mobile menu panel */}
          <div className="md:hidden fixed top-16 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg">
            <nav className="px-4 py-2 space-y-1">
              {publicNavItems.map((item) => (
                item.show && (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-3 py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link
                to="/login"
                className="block px-3 py-2 text-base font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors mt-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Link>
            </nav>
          </div>
        </>
      )}
    </header>
  )
}

export default Header
